import React, { useEffect, useRef, useState } from "react";
import LeaguePlayTopBar from "./LeaguePlayTopBar";
import { descriptionToText } from "./leagueStatus";
import { getLeagueMedia } from "./leagueImage";
import { assetSelectionLabel, durationLabel } from "./leagueFormat";
import { memberCount } from "./inviteCode";
import { SmallCheckCircleIcon } from "./icons";

// What the invite shows you before you commit: the league's own art, who's in it,
// how long it runs, and the rules you'd be agreeing to.
//
// Desktop and mobile are different shapes on purpose, per the designs. Desktop
// puts the whole thing on one card with a full-bleed hero and a four-across stat
// strip; mobile stacks separate cards and turns the strip into a 2x2 of chips,
// because four columns of label-over-value don't survive a 390px screen.

// The three facts about the league itself. Who created it is a person, not a
// statistic, so it gets its own row below with a face against the name — it read
// as a fourth number when it sat in the strip.
const STATS = [
  { key: "duration", label: "Duration" },
  { key: "asset", label: "Asset Type" },
  { key: "members", label: "Members" },
];

// Client Rev 2, private league landing, both breakpoints:
//   • "Cap the collapsed description at 3 rendered lines."
//   • "Show Read more only when the description exceeds 3 lines."
//   • "Fix description rendering so organizer entered line breaks and spacing
//      are preserved."
//
// Three fixes, and the third is why the first two were wrong:
//
// 1) LINE BREAKS. The text now arrives from descriptionToText with real newlines
//    in it, so the paragraph carries `whitespace-pre-line` — without it HTML
//    collapses every newline to a space and the organiser's rules run together
//    again ("League Rules:Be niceDont be a dick", from the client's screenshot).
//
// 2) THREE LINES. `line-clamp-3` now applies at BOTH breakpoints. It used to be
//    `line-clamp-3 md:line-clamp-none`, i.e. desktop printed the whole thing —
//    the client asks for the collapsed limit on desktop too ("Show Read more only
//    when the description exceeds the collapsed limit").
//
// 3) READ MORE, ONLY WHEN NEEDED. The old button rendered whenever the text was
//    non-empty, so a one-line description still offered a "Read more" that
//    expanded nothing. There is no CSS test for "did line-clamp actually clamp",
//    so this measures it: on the clamped paragraph, scrollHeight > clientHeight
//    means there is more text than three lines can show. Re-measured on resize
//    and once webfonts land, because the same string wraps to a different number
//    of lines at a different width.
const DescriptionText = ({ text, clampDesktop = true }) => {
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const measure = () => {
      const node = ref.current;
      // Only meaningful while the clamp is on — an expanded element is its full
      // height by definition and would always report "not clamped".
      if (!node || expanded) return;
      setClamped(node.scrollHeight - node.clientHeight > 1);
    };
    measure();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, expanded, clampDesktop]);

  if (!text) return null;

  return (
    <>
      <p
        ref={ref}
        className={`text-sm text-secondary leading-relaxed whitespace-pre-line ${
          expanded ? "" : clampDesktop ? "line-clamp-3" : "line-clamp-3 md:line-clamp-none"
        }`}
      >
        {text}
      </p>
      {!expanded && clamped && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="block ml-auto md:mx-auto mt-2 text-sm font-semibold text-[#0F4146] hover:underline"
        >
          Read more
        </button>
      )}
    </>
  );
};

// The Private League card.
//
// Client, this revision: "Keep the 'Private League' title and subtitle as a
// left-aligned header. Under the header will be [the six details]... Make the
// Private League card now a full-width card under the description, like the
// public league."
//
// WHAT CHANGED, and why the old construction went with it:
//
// This card used to be half of a two-column desktop grid, sitting beside the
// badge card, and it was built as a `[2.25rem_1fr]` grid so that the heading,
// the schedule rows and three check rows all shared one left edge inside a
// narrow column (client Rev 2: "Create one text column... Right now the
// description jumps farther left than everything else"). It also carried a
// hand-tuned `mt-1.5` whose only job was to start the schedule at roughly the
// same height as the badges in the card NEXT to it.
//
// Both of those existed to solve problems the new layout does not have: there is
// no adjacent card to line up with any more, and the details block brings its own
// two-line rows with their own left edge. So the grid and the offset are gone
// rather than carried forward — keeping them would be keeping the scaffolding of
// a layout that no longer exists.
//
// The three check rows are gone too. The client enumerated the card's contents as
// exactly six items, and two of the three checks (JOIN AFTER START, PORTFOLIO
// VISIBILITY) are among them, stated the same way the public page states them.
// The third — "Only members can see inside" — has no slot in the six. See the
// note beside `copy.closing` in LeagueJoinPreview.
const RuleCard = ({ icon, title, titleColor = "#0F172A", lead, details }) => (
  <div className="rounded-2xl border border-gray-200 bg-[#F8FAFA] p-4 md:p-5">
    <div className="flex items-start gap-2.5">
      <span className="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        {icon}
      </span>
      {/* Client Rev 2: "Make Private League use the same section heading size and
          weight as Podium rewards and Organizer prize. Those are peer sections,
          so they should share a heading token." PodiumRewardsCard's heading is
          Montserrat bold at text-base, so this is that token.
          Client Rev 5 put the subtitle on the secondary grey, at mt-0.5 — the
          same three tokens Podium's subtitle uses, so the two are identical by
          construction rather than by two hex values that happen to agree. */}
      <div className="min-w-0">
        <h3
          className="font-montserrat font-bold text-base"
          style={{ color: titleColor }}
        >
          {title}
        </h3>
        {lead && <p className="mt-0.5 text-sm text-secondary">{lead}</p>}
      </div>
    </div>

    {/* Client: "use the same ... divider treatment already established on the
        page" — which on the public landing card is a hairline with equal padding
        either side, between the header block and the six details. */}
    {details && <div className="mt-4 border-t border-divider pt-4">{details}</div>}
  </div>
);

// `titleInHeader` — the host is showing the league's name as the page title above
// this card (the private invite flow does on mobile), so the card must not repeat
// it there. Desktop keeps it either way: that layout has no page title.
//
// `identityInShell` — the host is a league screen INSIDE the league, where the
// shell already shows the art, the name and the clock at the top of the page. The
// card then drops its whole identity block and opens on the facts, so the league
// isn't introduced to a member who's standing in it.
const LeagueInvitePreviewCard = ({
  game,
  rules,
  reward,
  extra,
  footer,
  titleInHeader = false,
  identityInShell = false,
  headerInHost = false,
  // The desktop header row's share icon — see LeaguePlayTopBar. A private
  // league's link is an invitation, so only its organiser is offered it; the
  // host decides that and passes the answer in.
  showShare = false,
}) => {
  const media = getLeagueMedia(game);
  // Client Rev 2: "Change DURATION values to sentence case format such as 6
  // days." durationLabel already returns "6 days"; the deployed build the client
  // screenshotted ("<1 Week") predates that, so nothing to change here — the
  // value is read straight from it.
  //
  // ASSET TYPE now names the unit, and differs by breakpoint on the client's
  // instruction — see assetSelectionLabel. Both strings are computed and the
  // markup shows one per breakpoint, so there is no JS width check to get wrong.
  const values = {
    duration: durationLabel(game) || "—",
    asset: assetSelectionLabel(game, "mobile"),
    members: `${memberCount(game)} joined`,
  };
  const assetDesktop = assetSelectionLabel(game, "desktop");
  // descriptionToText, not stripHtml — the organiser's paragraph breaks have to
  // survive (client Rev 2). See leagueStatus.js.
  const description = descriptionToText(game.description);

  return (
    /* Client: "Let's make this easy. Match the public league landing page layout
       (104309). The only difference is that this one keeps the Organizer prize
       card as it is now, full width under the badge card."

       So the outer desktop card is gone. The public landing page is a STACK of
       separate cards at every width — `space-y-4`, no wrapper — and this page was
       a single bordered container on desktop with a full-bleed hero and a
       two-column grid inside it. Keeping the wrapper and stacking inside it would
       have produced cards-within-a-card, which is not the layout being matched.

       The order is the client's list, top to bottom:
         hero (private only — this is still the invite reveal)
         metabar          "Move the metabar up above the description and place it
                           in a container."
         description
         Private League   "now a full-width card under the description"
         badges           "full-width, like the public league"
         Organizer prize  "full width under the badge card"
         CTA */
    <div className="space-y-4">
      {/* The same desktop header row the public and cash landings carry — back
          arrow at the content column's left edge, share at its right. Client:
          "Public and private league previews should match."

          Dropped when the host already owns the screen's chrome
          (`identityInShell`), exactly as the public card does. */}
      {!identityInShell && (
        <LeaguePlayTopBar
          game={game}
          showShare={showShare}
          fallback="/contest"
          className="mb-2"
        />
      )}

      {/* Client Rev 2: "If no organizer image was uploaded, collapse the image
          area completely." getLeagueMedia falls back to a bundled Default.svg, so
          `hasUploadedMedia` is the test rather than the url being present.
          Sizing is Rev 2's too: mobile keeps its existing h-36 ("Keep the current
          mobile image aspect ratio... Do not force the wider desktop ratio onto
          mobile"), desktop is a responsive 3:1, and both use object-cover with
          centred positioning. */}
      {!identityInShell && media.hasUploadedMedia && (
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-[#F3F4F6]">
          <img
            src={media.url}
            alt=""
            className="w-full h-36 md:h-auto md:aspect-[3/1] object-cover object-center"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
          {/* Desktop badges the hero — unless the host is printing the
              "YOU FOUND THE LEAGUE" eyebrow above the card itself, in which case
              this would say it twice. Mobile always says it in the page title. */}
          {!headerInHost && (
            <span className="hidden md:inline-flex absolute top-4 left-4 items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-[#0F172A] backdrop-blur-sm">
              <SmallCheckCircleIcon className="w-4 h-4" />
              You Found the League
            </span>
          )}
        </div>
      )}

      {/* The title, for a host that has not already printed it. Left-aligned, as
          the public landing is. */}
      {!identityInShell && !headerInHost && (
        <h2
          className={`font-montserrat font-bold text-xl md:text-2xl text-[#0F172A] leading-tight ${
            titleInHeader ? "hidden md:block" : ""
          }`}
        >
          {game.title}
        </h2>
      )}

      {/* THE METABAR, now above the description and in its own container.
          Client: "Keep the top metadata bar unchanged with Duration, Asset type,
          Members" — so the three stats, the dividers and the wording are exactly
          what they were; only its position and its container changed. This is the
          public landing page's own metabar markup (`!p-0`, three columns on one
          divided row), so the two pages cannot drift. */}
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="grid grid-cols-3 divide-x divide-divider py-4">
          {STATS.map(({ key, label }) => (
            <div key={key} className="px-2 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary">
                {label}
              </p>
              {/* ASSET TYPE is the one stat whose wording differs by breakpoint,
                  on the client's own instruction: mobile "7 coins", desktop
                  "Crypto · 7 coins". Both are rendered and CSS picks one, so the
                  value can never disagree with the width it is shown at. */}
              {key === "asset" ? (
                <>
                  <p className="mt-0.5 md:hidden text-sm font-bold text-[#0F172A] truncate">
                    {values.asset}
                  </p>
                  <p className="mt-0.5 hidden md:block text-sm font-bold text-[#0F172A] truncate">
                    {assetDesktop}
                  </p>
                </>
              ) : (
                <p className="mt-0.5 text-sm font-bold text-[#0F172A] truncate">
                  {values[key]}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* The organiser's own words, in their own card — the public page's
          description card with the details lifted out of it, because on this page
          the details belong to the Private League card below (client's list). */}
      {description && (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          {/* The 3-line cap on DESKTOP is the invite landing page's rule (client
              Rev 2). League Details renders this same card with `identityInShell`
              and was never part of that request, so it keeps the full description
              on desktop exactly as before — mobile clamps on both screens, as it
              always did. */}
          <DescriptionText text={description} clampDesktop={!identityInShell} />
        </div>
      )}

      {/* Full width now, one under the other, rather than two desktop columns. */}
      {rules}
      {reward}
      {extra}
      {footer}
    </div>
  );
};

export { RuleCard };
export default LeagueInvitePreviewCard;
